import React, { useState, useEffect } from 'react';
import UnifiedDropdown from '../toggles/Dropdown/Dropdown';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Create a context to manage loading state across components
export const LoadingContext = React.createContext({
  setLoading: () => {},
  isLoading: false
});

const ChartControls = ({
  isDeviceIdsLoading,
  selectedStoreId,
  setSelectedStoreId,
  storeIds,
  sitePairings,
  storesWithData,
  dateRange,
  setDateRange,
  availableData,
  isLoadingAvailability,
  refreshAvailability,
  // New props for device and deck filters
  selectedDeviceId,
  setSelectedDeviceId,
  deviceOptions,
  deviceMapping,
  isDevicesLoading,
  selectedDeckId,
  setSelectedDeckId,
  deckOptions,
  deckMapping,
  isDecksLoading,
  // Optional smart handlers (if provided, use these instead of direct setState)
  onSiteChange,
  onDeckChange,
  onDeviceChange,
  onDateRangeChange
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Detect viewport size - three breakpoints: desktop (1560+), tablet (1024-1559), mobile (<1024)
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop', 'tablet', 'mobile'

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      if (width >= 1560) {
        setViewMode('desktop');
        setIsMobile(false);
      } else if (width >= 1024) {
        setViewMode('tablet');
        setIsMobile(true);
      } else {
        setViewMode('mobile');
        setIsMobile(true);
      }
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Use smart handlers if provided, otherwise fall back to direct setState
  const handleSiteChange = (newSiteId) => {
    console.log('ChartControls.handleSiteChange called:', { newSiteId, hasOnSiteChange: !!onSiteChange });
    if (onSiteChange) {
      // Use smart handler from parent (handles auto-toggle, etc.)
      console.log('  → Calling onSiteChange from parent');
      onSiteChange(newSiteId);
    } else {
      // Fallback to direct setState
      console.log('  → Using fallback setSelectedStoreId');
      setSelectedStoreId(newSiteId);
    }
    // Refresh availability after site change
    if (refreshAvailability) {
      console.log('  → Scheduling refreshAvailability');
      setTimeout(() => refreshAvailability(), 50);
    }
  };

  const handleDeckChange = (newDeckId) => {
    if (onDeckChange) {
      // Use smart handler from parent
      onDeckChange(newDeckId);
    } else {
      // Fallback to direct setState
      setSelectedDeckId(newDeckId);
    }
  };

  const handleDeviceChange = (newDeviceId) => {
    if (onDeviceChange) {
      onDeviceChange(newDeviceId);
    } else {
      setSelectedDeviceId(newDeviceId);
    }
  };

  const handleDateRangeChange = (newDateRange) => {
    if (onDateRangeChange) {
      onDateRangeChange(newDateRange);
    } else {
      setDateRange(newDateRange);
    }
    // useSiteAvailability will automatically refresh via useEffect
  };

  // Define filter tabs
  const filterTabs = [
    {
      id: 'deck',
      label: 'Deck',
      display: deckOptions && deckOptions.length > 0 && !isDeviceIdsLoading
    },
    {
      id: 'site',
      label: 'Site',
      display: true
    },
    {
      id: 'device',
      label: 'Device',
      display: deviceOptions && deviceOptions.length > 0 && !isDeviceIdsLoading
    },
    {
      id: 'date',
      label: 'Date',
      display: true
    }
  ].filter(tab => tab.display);

  // Desktop styles - 4 columns in 1 row
  const desktopContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '15px',
    width: '100%',
    maxWidth: '100%'
  };

  const desktopDropdownStyle = {
    width: '100%'
  };

  // Mobile carousel styles
  const mobileContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    alignItems: viewMode === 'tablet' ? 'flex-end' : 'stretch'
  };

  const mobileCarouselStyle = {
    display: 'flex',
    alignItems: 'stretch',
    gap: '4px',
    width: 'auto',
    minHeight: '60px',
    marginLeft: 'auto',
    flex: '0 1 auto',
    minWidth: viewMode === 'tablet' ? '530px' : 'auto',
    maxWidth: viewMode === 'tablet' ? 'calc(100% - 20px)' : '100%'
  };

  const carouselViewportStyle = {
    flex: viewMode === 'tablet' ? '0 0 480px' : '1 1 auto',
    overflow: 'hidden',
    overflowY: 'visible',
    borderRadius: '8px',
    width: viewMode === 'tablet' ? '480px' : '100%',
    minWidth: 0,
    maxWidth: 'none',
    position: 'relative',
    zIndex: 1
  };

  // For tablet (2 columns), each item ~40% width for ample spacing
  // For mobile (1 column), item takes 100% width
  const itemsPerView = viewMode === 'tablet' ? 1 : 1;
  const slideWidthPercent = viewMode === 'tablet' ? 35 : 100;

  const carouselContentStyle = {
    display: 'flex',
    transition: 'transform 0.3s ease-in-out',
    transform: `translateX(-${activeTab * slideWidthPercent}%)`,
    width: `${filterTabs.length * slideWidthPercent}%`
  };

  const carouselSlideStyle = {
    flex: `0 0 ${slideWidthPercent}%`,
    padding: '0 1px',
    minWidth: 0,
    width: `${slideWidthPercent}%`,
    boxSizing: 'border-box'
  };

  const navButtonStyle = (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 0,
    color: disabled ? '#cbd5e1' : '#64748b',
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.5 : 1,
    zIndex: 10,
    position: 'relative',
    flexShrink: 0
  });

  const tabDotsStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '8px'
  };

  const dotStyle = (isActive) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: isActive ? '#3b82f6' : '#cbd5e1',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  });

  // Labeled dropdown wrapper component
  const LabeledDropdown = ({ label, children }) => (
    <fieldset style={{
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      padding: '3px 6px',
      margin: 0,
      minWidth: viewMode === 'tablet' ? '70px' : isMobile ? '140px' : '160px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <legend style={{
        fontSize: '10px',
        fontWeight: '500',
        color: '#64748b',
        padding: '0 2px'
      }}>
        {label}
      </legend>
      {children}
    </fieldset>
  );

  // Render individual dropdown components
  const renderDeckDropdown = () => (
    !isDeviceIdsLoading && deckOptions && deckOptions.length > 0 && (
      <LabeledDropdown label="Deck">
        {isDecksLoading ? (
          <div className="loading-indicator" style={{
            padding: '12px 16px',
            background: '#f8fafc',
            fontSize: '14px',
            color: '#64748b',
            textAlign: 'center'
          }}>
            Loading decks...
          </div>
        ) : (
          <UnifiedDropdown
            type="deck"
            selectedValue={selectedDeckId}
            setSelectedValue={handleDeckChange}
            options={['all', ...deckOptions]}
            displayMapping={{
              'all': 'All Decks',
              ...deckMapping
            }}
            displayConfig={{
              mobileOptimized: isMobile,
              width: '70%',
              minWidth: '90px'
            }}
          />
        )}
      </LabeledDropdown>
    )
  );

  const renderSiteDropdown = () => (
    <LabeledDropdown label="Site">
      {isDeviceIdsLoading ? (
        <div className="loading-indicator" style={{
          padding: '12px 16px',
          background: '#f8fafc',
          fontSize: '14px',
          color: '#64748b',
          textAlign: 'center'
        }}>
          Loading sites...
        </div>
      ) : (
        <UnifiedDropdown
          type="site"
          selectedValue={selectedStoreId}
          setSelectedValue={handleSiteChange}
          options={storeIds}
          displayMapping={sitePairings}
          availabilityData={storesWithData}
          displayConfig={{
            className: isLoadingAvailability ? 'loading-availability' : '',
            mobileOptimized: isMobile,
            width: '100%',
            minWidth: '90px'
          }}
        />
      )}
    </LabeledDropdown>
  );

  const renderDeviceDropdown = () => (
    !isDeviceIdsLoading && deviceOptions && deviceOptions.length > 0 && (
      <LabeledDropdown label="Device">
        {isDevicesLoading ? (
          <div className="loading-indicator" style={{
            padding: '12px 16px',
            background: '#f8fafc',
            fontSize: '14px',
            color: '#64748b',
            textAlign: 'center'
          }}>
            Loading devices...
          </div>
        ) : (
          <UnifiedDropdown
            type="device"
            selectedValue={selectedDeviceId}
            setSelectedValue={handleDeviceChange}
            options={['all', ...deviceOptions]}
            displayMapping={{
              'all': 'All Devices',
              ...deviceMapping
            }}
            displayConfig={{
              mobileOptimized: isMobile,
              width: '100%',
              minWidth: '90px'
            }}
          />
        )}
      </LabeledDropdown>
    )
  );

  const renderDateDropdown = () => (
    !isDeviceIdsLoading && (
      <LabeledDropdown label="Date">
        <UnifiedDropdown
          type="dateRange"
          selectedValue={dateRange}
          setSelectedValue={handleDateRangeChange}
          options={[
            { value: 'today', label: 'Today' },
            { value: 'yesterday', label: 'Yesterday' },
            { value: 'week', label: 'Past week' },
            { value: 'month', label: 'Past month' },
            { value: 'year', label: 'Past year' },
            { value: 'all', label: 'All time' }
          ]}
          availabilityData={availableData}
          displayConfig={{
            mobileOptimized: isMobile,
            width: '100%',
            minWidth: '90px'
          }}
        />
      </LabeledDropdown>
    )
  );

  const getDropdownForTab = (tabId) => {
    switch (tabId) {
      case 'deck':
        return renderDeckDropdown();
      case 'site':
        return renderSiteDropdown();
      case 'device':
        return renderDeviceDropdown();
      case 'date':
        return renderDateDropdown();
      default:
        return null;
    }
  };

  // Desktop render
  if (!isMobile) {
    return (
      <div style={desktopContainerStyle} className="chart-controls-desktop">
        {deckOptions && deckOptions.length > 0 && !isDeviceIdsLoading && (
          <div style={desktopDropdownStyle}>
            {renderDeckDropdown()}
          </div>
        )}
        <div style={desktopDropdownStyle}>
          {renderSiteDropdown()}
        </div>
        {deviceOptions && deviceOptions.length > 0 && !isDeviceIdsLoading && (
          <div style={desktopDropdownStyle}>
            {renderDeviceDropdown()}
          </div>
        )}
        <div style={desktopDropdownStyle}>
          {renderDateDropdown()}
        </div>
        {isLoadingAvailability && (
          <div style={{
            padding: '8px 12px',
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400e',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            flex: '0 1 auto'
          }}>
            Checking data availability...
          </div>
        )}
      </div>
    );
  }

  // Calculate max tab position based on items per view
  const maxTabIndex = Math.max(0, filterTabs.length - itemsPerView);

  // Mobile carousel render
  return (
    <div style={mobileContainerStyle} className="chart-controls-mobile">
      {/* Mobile Carousel */}
      <div style={mobileCarouselStyle}>
        {/* Previous Button */}
        <button
          style={navButtonStyle(activeTab === 0)}
          onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
          disabled={activeTab === 0}
          title="Previous filter"
        >
          <ChevronLeft size={18} />
        </button>

        {/* Carousel Viewport */}
        <div style={carouselViewportStyle}>
          <div style={carouselContentStyle}>
            {filterTabs.map((tab) => (
              <div key={tab.id} style={carouselSlideStyle}>
                {getDropdownForTab(tab.id)}
              </div>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <button
          style={navButtonStyle(activeTab === maxTabIndex)}
          onClick={() => setActiveTab(Math.min(maxTabIndex, activeTab + 1))}
          disabled={activeTab === maxTabIndex}
          title="Next filter"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Dot Navigation - only show for mobile (1 item view) */}
      {viewMode === 'mobile' && (
        <div style={{...tabDotsStyle, alignSelf: 'center'}}>
          {filterTabs.map((_, index) => (
            <div
              key={index}
              style={dotStyle(activeTab === index)}
              onClick={() => setActiveTab(index)}
              title={`Go to ${filterTabs[index].label}`}
            />
          ))}
        </div>
      )}

      {/* Loading indicator for mobile */}
      {isLoadingAvailability && (
        <div style={{
          padding: '8px 12px',
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#92400e',
          textAlign: 'center',
          alignSelf: viewMode === 'tablet' ? 'flex-end' : 'stretch'
        }}>
          Checking data availability...
        </div>
      )}
    </div>
  );
};

export default ChartControls;
